import { metaObjectsFormat } from '../metadata-format';

interface StepMetaObject {
  id: string | number;
  name?: string;
  type?: string;
  parent?: string | number | null;
}

interface StepPropertySet {
  name?: string;
  type?: string;
}

interface StepProperty {
  value?: unknown;
  valueType?: string;
}

// Only metaObjects are part of the contract; the two sibling arrays are read loosely so every
// caller's own metadata type fits without restating it here.
interface StepMetadata {
  metaObjects?: StepMetaObject[];
}

type StepDocument = StepMetadata & {
  propertySets?: StepPropertySet[];
  properties?: StepProperty[];
};

/** STEP entity line, e.g. `#561=annotation_plane(Linear Size.1,(#3460),#741,(#607))`. */
const STEP_ENTITY_LINE = /^#\d+\s*=/;

/** STEP's "unset" marker, emitted by the converter for names, values and value types alike. */
const STEP_UNSET = '$';

const baseId = (id: string) => id.split(':')[0];

// Adapting is pure, so the result is cached against the metadata the loader parsed, the way
// metadata-graph.ts caches its own derived index.
const metaObjectsCache = new WeakMap<object, StepMetaObject[]>();
const metadataCache = new WeakMap<object, StepMetadata>();

/**
 * Label carried by a raw STEP entity line, i.e. its first attribute that is not a reference
 * (`#4351`), an unset value (`$`, `*`) or an entity type name (the token before a `(`).
 * `#3567=manifold_solid_brep(PartBody,#3566)` -> `PartBody`.
 */
export function stepLabel(name: string | undefined): string | undefined {
  if (!name || !STEP_ENTITY_LINE.test(name)) return undefined;

  for (const [, token, delimiter] of name
    .slice(name.indexOf('=') + 1)
    .matchAll(/([^(),]*)([(),])/g)) {
    const label = token.trim();
    if (!label || delimiter === '(' || label === '$' || label === '*' || label.startsWith('#'))
      continue;
    return label;
  }

  return undefined;
}

export function stepNodeName(metaObject: StepMetaObject): string {
  const name = metaObject.name === STEP_UNSET ? undefined : metaObject.name;
  const label = name && STEP_ENTITY_LINE.test(name) ? stepLabel(name) : name;
  return label || metaObject.type || String(metaObject.id);
}

/**
 * STEP metaObjects with readable names instead of raw entity lines, and parent ids that
 * actually resolve. Returns the input untouched for any other format.
 */
export function adaptStepMetaObjects<T extends StepMetaObject>(metaObjects: T[]): T[] {
  if (metaObjectsFormat(metaObjects) !== 'step') return metaObjects;

  const cached = metaObjectsCache.get(metaObjects);
  if (cached) return cached as T[];

  // First instance wins for a shared base id; the converter emits one parent per entity, so a
  // per-instance mapping would need data the JSON does not carry.
  const idByBase = new Map<string, string>();
  for (const metaObject of metaObjects) {
    const id = String(metaObject.id);
    if (!idByBase.has(baseId(id))) idByBase.set(baseId(id), id);
  }

  const adapted = metaObjects.map((metaObject) => {
    // The converter drops the instance suffix on parent refs (`#13` for `#13:0`), so a plain
    // id lookup orphans every child.
    const parent = metaObject.parent == null ? metaObject.parent : String(metaObject.parent);
    return {
      ...metaObject,
      name: stepNodeName(metaObject),
      parent: parent == null ? parent : (idByBase.get(baseId(parent)) ?? parent),
    };
  });

  metaObjectsCache.set(metaObjects, adapted);
  return adapted;
}

/**
 * The whole STEP metadata document normalized into the shape the IFC readers already
 * understand. Returns the input untouched for any other format.
 */
export function adaptStepMetadata<T extends StepMetadata>(metadata: T): T {
  if (!metadata.metaObjects || metaObjectsFormat(metadata.metaObjects) !== 'step') return metadata;

  const cached = metadataCache.get(metadata);
  if (cached) return cached as T;

  const document = metadata as StepDocument;
  const adapted = {
    ...metadata,
    metaObjects: adaptStepMetaObjects(metadata.metaObjects),
    // Property set names are raw entity lines too, and their entity type is the more useful
    // heading than the first attribute a line happens to carry.
    propertySets: document.propertySets?.map((propertySet) => ({
      ...propertySet,
      name: propertySet.type || stepLabel(propertySet.name) || propertySet.name,
    })),
    properties: document.properties?.map((property) => ({
      ...property,
      value: property.value === STEP_UNSET ? '' : property.value,
      valueType: property.valueType === STEP_UNSET ? '' : property.valueType,
    })),
  } as T;

  metadataCache.set(metadata, adapted);
  return adapted;
}
